import { Fragment, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import CompareSection from './compare-section'
import { GroupTable, OverallExcluded, RowsExcluded } from './group-table'
import { KpiCards, KpiSkeleton } from './kpi-cards'
import LtvWindowsTable from './ltv-windows-table'
import { ExclusionSheet, GlossarySheet } from './page-sheets'
import { PaybackHeader, scopeSearch, usePaybackSearch } from './payback-header'
import { PidTable } from './pid-table'
import { ScopePicker, useRoasTree } from './scope-picker'
import { ScopeList, scopeName } from './scope-list'
import { availableNode } from './scope-tree'
import { usePayback } from './use-payback'

// MON-93 子表随 node 层级：根与有子分组的产品线 → 业务群，其余分组 → 产品对比，pid → 国家对比
const BREAKDOWN = {
  group: { title: '业务群', firstColumn: '业务群' },
  pid: { title: '产品对比', firstColumn: 'pid' },
  country: { title: '国家对比', firstColumn: '国家' },
}

function Breakdown({ data, onSelect }) {
  const { breakdown, range } = data
  const total = data.rows[0]
  const label = scopeName(range)
  if (!breakdown.rows.length) return null
  if (breakdown.kind === 'pid') {
    return <PidTable rows={breakdown.rows} total={total} totalLabel={label} onSelect={onSelect} />
  }
  return (
    <GroupTable
      {...BREAKDOWN[breakdown.kind]}
      rows={breakdown.rows}
      total={total}
      totalLabel={label}
      onSelect={breakdown.kind === 'country' ? undefined : onSelect}
    />
  )
}

function Overview({ data, query, onSelect }) {
  const row = data.rows[0]
  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <ScopeList data={data} />
      <KpiCards row={row} />
      <CompareSection query={query} asOf={data.asOf} />
      <LtvWindowsTable
        key={`${data.range.node}:${(data.range.countries ?? []).join(',')}`}
        cohortForecast={data.cohortForecast}
        scopes={[{ id: 'total', name: scopeName(data.range), windows: row.windows }, ...data.breakdown.rows]}
      />
      <Breakdown data={data} onSelect={onSelect} />
    </div>
  )
}

/** 面包屑：根 → 当前节点，点上层回到该节点（国家与截止日不变） */
function ScopeBreadcrumb({ range, countries, asOf }) {
  const path = range.path.map((step, index) => (index === 0 ? { ...step, name: '全盘' } : step))
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {path.map((step, index) => (
          <Fragment key={step.node}>
            {index > 0 && <BreadcrumbSeparator />}
            <BreadcrumbItem>
              {index === path.length - 1 ? (
                <BreadcrumbPage>{step.name}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={scopeSearch({ node: step.node, countries, asOf })}>{step.name}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          </Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

/** MON-93 资金回收页：范围 = 产品树节点 × 国家，状态全在 URL（node / countries / as_of） */
export default function PaybackPage() {
  const { node, countries, asOf, update } = usePaybackSearch()
  const { tree, error: treeError, isLoading: treeLoading } = useRoasTree()
  const selectedNode = tree ? availableNode(tree.groups, node) : node
  const query = tree && { node: selectedNode, countries: countries.join(','), asOf }
  const payback = usePayback(query)
  const data = payback.data
  useEffect(() => {
    if (tree && selectedNode !== node) update({ node: selectedNode }, { replace: true })
  }, [tree, selectedNode, node, update])
  // 点子行 = node 收窄一层，国家不变
  const narrow = (next) => update({ node: next })

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 lg:p-6">
      <PaybackHeader
        actions={
          <>
            <ScopePicker tree={tree} node={selectedNode} countries={countries} onChange={update} />
            {/* MON-68 排除说明与术语收进页头按钮：当前范围排除说明 + 下一层排除格子 */}
            <ExclusionSheet count={data ? data.excluded.length : null}>
              {data && (
                <>
                  <OverallExcluded title={`${scopeName(data.range)}排除说明`} data={data} row={data.rows[0]} />
                  {data.breakdown.rows.length > 0 && (
                    <RowsExcluded
                      title={`${BREAKDOWN[data.breakdown.kind].title}排除格子`}
                      firstColumn={BREAKDOWN[data.breakdown.kind].firstColumn}
                      breakdown={data.breakdown}
                    />
                  )}
                </>
              )}
            </ExclusionSheet>
            <GlossarySheet formulas={data?.formulas} data={data} />
          </>
        }
      >
        {data && data.range.path.length > 1 && (
          <ScopeBreadcrumb range={data.range} countries={countries} asOf={asOf} />
        )}
        <h1 className="text-2xl font-semibold tracking-tight">
          ROAS{data && ` · ${scopeName(data.range)}`}
        </h1>
      </PaybackHeader>
      {(treeError || payback.error) && (
        <Alert variant="destructive">
          <AlertDescription>读取失败：{treeError || payback.error}</AlertDescription>
        </Alert>
      )}
      {(treeLoading || payback.isLoading) && <KpiSkeleton />}
      {data && !payback.isLoading && <Overview data={data} query={{ node: selectedNode, countries: query.countries }} onSelect={narrow} />}
    </div>
  )
}
